import { C } from "../../shared/theme";
import { MATRIX, MATRIX_WHEN, MATRIX_FEEL, COUCH, CYCLE } from "./learnContent";
import { hue, sectionLabel, pageTitle, body, intro, card, primaryButton } from "./learnStyles";

const MATRIX_ID = "matrix-heading";
const COUCH_ID = "couch-heading";
// Functions, not constants: C changes with the theme, so colours are read at render time.
const axisLabel = () => ({ ...sectionLabel(C.textMid), fontSize: 12 });
const para = () => ({ ...body, color: C.text });

// The four forces as a table: column headings Pain and Pleasure, row headings Present and
// Future. The row headings stand on their side so the cells keep the phone's width.
function Matrix() {
  return (
    <table aria-labelledby={MATRIX_ID} style={{ width: "calc(100% + 16px)", margin: -8, tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 8 }}>
      <colgroup><col style={{ width: 22 }} /><col /><col /></colgroup>
      <thead>
        <tr>
          <td aria-hidden="true" />
          {MATRIX_FEEL.map(feel => <th key={feel} scope="col" style={{ ...axisLabel(), textAlign: "center" }}>{feel}</th>)}
        </tr>
      </thead>
      <tbody>
        {MATRIX_WHEN.map(when => (
          <tr key={when}>
            <th scope="row" style={{ ...axisLabel(), padding: 0 }}>
              <span style={{ display: "inline-block", writingMode: "vertical-rl", transform: "rotate(180deg)" }}>{when}</span>
            </th>
            {MATRIX_FEEL.map(feel => MATRIX.find(c => c.when === when && c.feel === feel)).map(cell => {
              const h = hue(cell.hue);
              return (
                <td key={cell.id} style={{ verticalAlign: "top", padding: 12, borderRadius: 12, background: h.w, border: `1px solid ${h.b}`, color: C.text }}>
                  <strong style={{ display: "block", fontSize: 15, marginBottom: 4 }}>{cell.title}</strong>
                  <span style={{ display: "block", fontSize: 14, lineHeight: 1.45, marginBottom: 6 }}>{cell.feels}</span>
                  <span style={{ display: "block", fontSize: 14, lineHeight: 1.45 }}>{cell.text}</span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// "How motivation works": the forces behind every choice to start or put off, why the
// present wins, and the cycle that gets you moving.
export function FoundationsPage({ onOpen }) {
  const purple = hue("purple");
  const heading = (text, id) => <h2 id={id} style={{ ...sectionLabel(purple.c), margin: "10px 0 0" }}>{text}</h2>;
  return (
    <div style={{ padding: "4px 20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      <h1 tabIndex={-1} style={{ ...pageTitle, color: C.text, outline: "none" }}>How motivation works</h1>
      <p style={{ ...intro(), fontSize: 16, lineHeight: 1.55 }}>Two ideas sit under everything else in Learn. Once you know them, the reasons and the fixes make more sense.</p>

      {heading("Pain or pleasure, in the present or future", MATRIX_ID)}
      <p style={para()}>Everything that pulls you toward a task or pushes you away from it feels either painful or pleasant, and arrives either now or later. Which box it sits in decides how hard it pulls.</p>
      <Matrix />

      {heading("The present has an unfair advantage", COUCH_ID)}
      <p style={para()}>Say you mean to exercise, but you're comfortable on the couch watching TV. Your brain weighs it up something like this.</p>
      <ul aria-labelledby={COUCH_ID} style={{ ...card(), margin: 0, padding: 0, listStyle: "none" }}>
        {MATRIX.map(cell => (
          <li key={cell.id} style={{ padding: "11px 14px", borderBottom: `1px solid ${C.border}`, marginBottom: -1, display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: hue(cell.hue).c }}>{cell.title}</span>
            <span style={{ fontSize: 15, lineHeight: 1.45, color: C.text }}>{COUCH[cell.id]}</span>
          </li>
        ))}
      </ul>
      <p style={para()}>On paper, exercising is the better choice. <b>In the moment, the couch wins.</b></p>
      <p style={para()}>Your brain counts what happens now far more than what happens later. Psychologists call this <i>temporal discounting</i>. It's why smart, ambitious people keep making choices that work against their own goals.</p>

      {heading("Putting it off trades one pain for another")}
      <p style={para()}>When you put something off, you aren't always choosing pleasure over pain. Often you're choosing <b>less pain now for more pain later</b>.</p>
      <p style={para()}>Take your taxes. Doing them today means two hours of tedious work. Putting them off brings instant relief. But the task doesn't go away. It stays in the back of your mind as guilt and worry, and later as deadline pressure. You've borrowed comfort from your future self.</p>
      <p style={para()}>That relief feels like a reward. Each time you get it, putting things off becomes more of a habit.</p>

      {heading("Motivation and action feed each other")}
      <p style={para()}>You don't need to feel motivated before you start. Starting is what makes the motivation.</p>
      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
        {CYCLE.map((step, i) => (
          <li key={step.title} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 14px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}` }}>
            <span aria-hidden="true" style={{ width: 28, height: 28, flexShrink: 0, borderRadius: 14, background: purple.w, color: purple.c, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{i + 1}</span>
            <span style={{ fontSize: 15, lineHeight: 1.5, color: C.text }}><b>{step.title}</b> {step.text}</span>
          </li>
        ))}
      </ol>

      <button onClick={() => onOpen("why")} style={{ ...primaryButton(), marginTop: 8 }}>
        Next: why we put things off ›
      </button>
    </div>
  );
}
